import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Users, TrendingUp, DollarSign, BarChart3, Target, ShoppingCart, Settings, Brain, PieChart, ChevronDown, ChevronUp, Search, Map, Zap, Download, X, Loader2, Activity, Pill } from 'lucide-react';
import { BedrockAgentRuntimeClient, InvokeAgentCommand } from "@aws-sdk/client-bedrock-agent-runtime";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { SignatureV4 } from "@aws-sdk/signature-v4";
import { HttpRequest } from "@aws-sdk/protocol-http";
import { Sha256 } from "@aws-crypto/sha256-browser";
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ChartWithControls from '../chatbotui/chartwithcontrols';
import DataTable from './datatable';
import SQLValidator from '../../utils/SQLValidator';
import { buildSQLPrompt } from '../../utils/prompts';
import config from '../../amplifyconfiguration.json';

const GuiaInteractiva = () => {
    const [activeTab, setActiveTab] = useState('fases');
    const [completedSteps, setCompletedSteps] = useState(new Set());
    const [expandedSections, setExpandedSections] = useState(new Set([0]));

    // Estados para el modal y agente
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [agentResponse, setAgentResponse] = useState('');
    const [vizLoading, setVizLoading] = useState(false);
    const [queryData, setQueryData] = useState(null);
    const [currentMessageId, setCurrentMessageId] = useState(null);

    // Estado para el sistema de cache
    const [queryCache, setQueryCache] = useState({});

    // Usuario y configuración
    const { user } = useAuthenticator((context) => [context.user]);
    const id_user = user?.username || 'demo_user';
    const id_conversation = `guia_interactiva_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // URLs y configuración
    const baseURL = process.env.REACT_APP_BASE_URL;
    const baseURLBD = process.env.REACT_APP_BD;
    const apiKeyClaude = process.env.CLAUDE;
    const AGENT_ID = "UYF7CEYBIB";
    const AGENT_ALIAS_ID = "KGUVP9419C";

    // Endpoints
    const endpoint_add_message = '/add_message';
    const endpoint_get_sql = '/get_query';
    const endpoint_executeSQL = '/get_rds';

    // Funciones de cache
    const generateCacheKey = (query) => {
        return query.toLowerCase().trim().replace(/[^a-z0-9]/g, '_');
    };

    const saveToCache = (query, data) => {
        const cacheKey = generateCacheKey(query);
        setQueryCache(prev => ({
            ...prev,
            [cacheKey]: {
                ...data,
                timestamp: Date.now(),
                query: query
            }
        }));
    };

    const getFromCache = (query) => {
        const cacheKey = generateCacheKey(query);
        return queryCache[cacheKey];
    };

    const existsInCache = (query) => {
        const cacheKey = generateCacheKey(query);
        return !!queryCache[cacheKey];
    };

    const roleIcons = {
        "Director Médico": Activity,
        "Gerente de Marketing Farmacéutico": Target,
        "Analista de Market Access": Map,
        "Director Comercial": TrendingUp,
        "Gerente de Inteligencia de Mercado": Brain,
        "Especialista en Pricing": DollarSign,
        "Director de Ventas Regional": Users,
        "Analista de Clases Terapéuticas": Pill,
        "Gerente de Trade Marketing": ShoppingCart
    };

    const faseIcons = {
        "Exploración Inicial de Datos": Search,
        "Análisis de Prescripción Médica": Activity,
        "Análisis de Market Share por Geografía": Map,
        "Análisis de Clases Terapéuticas ATC": Pill,
        "Performance de Laboratorios": BarChart3,
        "Análisis Cross-Dimensional": Brain
    };

    const consultasPorRol = [
        {
            title: "Director Médico",
            description: "Análisis de prescripción, cobertura médica y performance por especialidad",
            priority: "Alta",
            timeEstimate: "15-20 min",
            consultas: [
                "¿Cuáles son los médicos con mayor número de prescripciones (rx) por especialidad (SPECIALTY_ID_1) en los últimos 6 meses del período disponible?",
                "Analiza la relación entre prescripciones (rx) y dispensaciones (dx) por clase terapéutica ATC4 (atc4_desc) durante todo el período 2023-2024",
                "¿Qué regiones (doctor_region_description) tienen la mayor cobertura de médicos visitados (VISITS_NUMBER) y cómo se relaciona con las prescripciones?"
            ]
        },
        {
            title: "Gerente de Marketing Farmacéutico",
            description: "Estrategias de posicionamiento de productos y análisis de portafolio",
            priority: "Alta",
            timeEstimate: "18-25 min",
            consultas: [
                "¿Cuál es el performance de productos genéricos (gene_cod = 'G') vs marca (gene_cod = 'M') por clase terapéutica atc4_desc en unidades vendidas (units)?",
                "Identifica oportunidades de crecimiento analizando productos (product_desc) con baja penetración pero alta prescripción por estado (state_desc)",
                "¿Qué moléculas (molecula) tienen mayor potencial basándose en la tendencia de prescripciones y ventas en el último año?"
            ]
        },
        {
            title: "Analista de Market Access",
            description: "Análisis de acceso al mercado y penetración geográfica",
            priority: "Alta",
            timeEstimate: "20-25 min",
            consultas: [
                "Analiza la penetración de mercado por brick (brick_code, brick_desc) comparando ventas (units) vs prescripciones (rx) en el período 2023-2024",
                "¿Qué estados (state_desc) muestran disparidad entre prescripción médica y ventas reales para identificar barreras de acceso?",
                "Compara la distribución geográfica de dispensaciones (dispensed_state_desc) vs ubicación del médico (doctor_state_desc)"
            ]
        },
        {
            title: "Director Comercial",
            description: "Análisis de performance comercial y estrategia competitiva",
            priority: "Alta",
            timeEstimate: "15-20 min",
            consultas: [
                "¿Cuál es el market share de los top 10 laboratorios (labdesc) por unidades vendidas (units) y valores (values) en el último trimestre disponible?",
                "Analiza la efectividad de la fuerza de ventas comparando número de visitas (VISITS_NUMBER) con prescripciones (rx) por región",
                "¿Qué corporativos (corp_desc) dominan el mercado ético (market = 'E') vs OTC/Popular (market = 'P')?"
            ]
        },
        {
            title: "Gerente de Inteligencia de Mercado",
            description: "Análisis competitivo avanzado y tendencias de mercado",
            priority: "Alta",
            timeEstimate: "25-30 min",
            consultas: [
                "Realiza un análisis competitivo por clase ATC4: market share, tendencias de crecimiento y principales laboratorios en cada segmento",
                "¿Cómo ha evolucionado la participación de mercado de productos por segmento (segment_flag: PH, BH, CH) durante 2023-2024?",
                "Identifica productos estrella vs productos en declive basándose en tendencias de prescripciones, dispensaciones y ventas"
            ]
        },
        {
            title: "Especialista en Pricing",
            description: "Análisis de precios y estrategias de valor",
            priority: "Media",
            timeEstimate: "20-25 min",
            consultas: [
                "Analiza la conversión de unidades pack a unidades estándar (std_fact) y dosis (dsg_fact) para optimización de pricing por producto",
                "¿Qué productos (pack_desc) tienen mayor valor promedio por unidad en cada clase terapéutica ATC4?",
                "Compara la composición de sustancia activa (stgh_val, stgh_mea) entre productos competidores de la misma molécula"
            ]
        },
        {
            title: "Director de Ventas Regional",
            description: "Optimización territorial y performance por región",
            priority: "Alta",
            timeEstimate: "15-20 min",
            consultas: [
                "¿Cuáles son las regiones (doctor_region_description) con mayor potencial sin explotar basándose en prescripciones altas pero ventas bajas?",
                "Analiza el performance de ventas por brick comparando diferentes estados y regiones del período 2023-2024",
                "¿Qué médicos (doctor_id, FIRST_NAME, LAST_NAME) son top prescriptores por región y no están siendo visitados efectivamente?"
            ]
        },
        {
            title: "Analista de Clases Terapéuticas",
            description: "Deep dive en áreas terapéuticas y clasificaciones ATC/NEC",
            priority: "Media",
            timeEstimate: "20-25 min",
            consultas: [
                "Analiza la evolución de ventas por los 4 niveles de clasificación ATC (atc1_desc, atc2_desc, atc3_desc, atc4_desc) durante 2023-2024",
                "¿Qué áreas terapéuticas muestran mayor crecimiento en prescripciones médicas y cuáles en ventas directas?",
                "Compara el performance de productos farma puro (segment_flag = 'PH') vs productos de consumo (segment_flag = 'CH') por clase terapéutica"
            ]
        },
        {
            title: "Gerente de Trade Marketing",
            description: "Análisis de canales y estrategias de distribución",
            priority: "Media",
            timeEstimate: "15-20 min",
            consultas: [
                "Analiza los patrones de dispensación por ubicación geográfica (dispensed_brick_desc, dispensed_region_description) vs ubicación del médico",
                "¿Qué productos tienen mayor dispersión geográfica en dispensaciones indicando mejor penetración de canal?",
                "Compara ventas por tipo de consultorio (TIPO_CONSULTORIO) para identificar canales más rentables"
            ]
        }
    ];

    const consultasPorFase = [
        {
            title: "Exploración Inicial de Datos",
            description: "Análisis exploratorio del mercado farmacéutico mexicano con datos IQVIA",
            priority: "Alta",
            timeEstimate: "20-25 min",
            consultas: [
                {
                    text: "Dame un resumen general de las principales métricas: total de prescripciones (rx), dispensaciones (dx), unidades vendidas (units) y valores (values) en el período 2023-2024",
                },
                {
                    text: "¿Cuáles son los top 10 laboratorios (labdesc) por unidades vendidas (units) y su participación de mercado en todo el período disponible?",
                },
                {
                    text: "Muestra la distribución de productos por tipo de mercado (market: Ético vs OTC/Popular) y por segmento (segment_flag: PH, BH, CH)",
                },
                {
                    text: "¿Cuántos médicos únicos (doctor_id) hay en la base y cómo se distribuyen por especialidad principal (SPECIALTY_ID_1)?",
                },
                {
                    text: "Analiza la distribución geográfica de ventas por estado (state_desc) y región (region_description) en el último año disponible",
                }
            ]
        },
        {
            title: "Análisis de Prescripción Médica",
            description: "Deep dive en comportamiento de prescripción y cobertura médica",
            priority: "Alta",
            timeEstimate: "18-22 min",
            consultas: [
                {
                    text: "¿Cuáles son los médicos con mayor número de prescripciones (rx) por especialidad (SPECIALTY_ID_1) y región (doctor_region_description)?",
                },
                {
                    text: "Analiza la relación entre número de visitas (VISITS_NUMBER) y prescripciones (rx) para medir efectividad de fuerza de ventas",
                },
                {
                    text: "¿Qué productos (product_desc) tienen mayor tasa de conversión de prescripción (rx) a dispensación (dx)?",
                },
                {
                    text: "Compara el perfil de prescripción entre especialidades principales (SPECIALTY_ID_1) por clase terapéutica ATC4 (atc4_desc)",
                },
                {
                    text: "Identifica médicos con alto potencial: muchas prescripciones pero pocas visitas (VISITS_NUMBER bajo)",
                }
            ]
        },
        {
            title: "Análisis de Market Share por Geografía",
            description: "Performance comercial y penetración territorial",
            priority: "Alta",
            timeEstimate: "25-30 min",
            consultas: [
                {
                    text: "¿Cuál es el market share por laboratorio (labdesc) en cada estado (state_desc) basándose en unidades vendidas (units) durante 2023-2024?",
                },
                {
                    text: "Analiza la evolución mensual (period) de ventas por región (region_description) para identificar tendencias estacionales",
                },
                {
                    text: "Compara la penetración por brick (brick_desc) entre diferentes corporativos (corp_desc) en los principales estados",
                },
                {
                    text: "¿Qué regiones tienen mayor disparidad entre prescripciones médicas y ventas reales?",
                },
                {
                    text: "Identifica oportunidades de crecimiento: estados con baja penetración de tu laboratorio pero alto potencial de mercado",
                }
            ]
        },
        {
            title: "Análisis de Clases Terapéuticas ATC",
            description: "Análisis profundo por clasificación terapéutica y moléculas",
            priority: "Alta",
            timeEstimate: "20-25 min",
            consultas: [
                {
                    text: "¿Cuáles son las clases terapéuticas ATC4 (atc4_desc) con mayor crecimiento en ventas (units, values) durante 2023-2024?",
                },
                {
                    text: "Analiza el market share por laboratorio dentro de cada clase terapéutica ATC4 principal",
                },
                {
                    text: "¿Qué moléculas (molecula) dominan en prescripciones (rx) vs dispensaciones (dx) vs ventas (units)?",
                },
                {
                    text: "Compara el performance de genéricos (gene_cod = 'G') vs marca (gene_cod = 'M') por cada nivel de clasificación ATC",
                },
                {
                    text: "Identifica clases terapéuticas emergentes con alta tasa de crecimiento en los últimos 6 meses del período",
                }
            ]
        },
        {
            title: "Performance de Laboratorios",
            description: "Análisis competitivo de fabricantes y corporativos",
            priority: "Media",
            timeEstimate: "15-20 min",
            consultas: [
                {
                    text: "Ranking de laboratorios (labdesc) por múltiples métricas: prescripciones, dispensaciones, unidades y valores en todo el período",
                },
                {
                    text: "¿Qué corporativos (corp_desc) tienen el portafolio más diversificado en términos de clases terapéuticas ATC?",
                },
                {
                    text: "Analiza la efectividad de lanzamientos: productos con fecha de lanzamiento (pack_lch, prod_lch) en los últimos 12 meses",
                },
                {
                    text: "Compara el performance de laboratorios en mercado ético (market = 'E') vs OTC/Popular (market = 'P')",
                },
                {
                    text: "¿Qué laboratorios tienen mayor presencia en productos farma puro (segment_flag = 'PH') vs híbridos (BH) vs consumo (CH)?",
                }
            ]
        },
        {
            title: "Análisis Cross-Dimensional",
            description: "Análisis integrado combinando prescripción, dispensación y ventas",
            priority: "Alta",
            timeEstimate: "25-30 min",
            consultas: [
                {
                    text: "Análisis de conversión completo: prescripción (rx) → dispensación (dx) → ventas (units) por producto y geografía",
                },
                {
                    text: "¿Qué productos tienen alta prescripción pero baja dispensación? ¿Y alta dispensación pero bajas ventas?",
                },
                {
                    text: "Compara médicos prescriptores en una región (doctor_state_desc) vs ubicación de dispensaciones (dispensed_state_desc) para análisis de flujo",
                },
                {
                    text: "Analiza la correlación entre visitas médicas (VISITS_NUMBER), prescripciones (rx) y ventas finales (units) por brick",
                },
                {
                    text: "Identifica productos con mejor ROI de inversión en fuerza de ventas basándose en conversión visitas → prescripción → ventas",
                },
                {
                    text: "Análisis de brechas: compara cobertura médica vs penetración de ventas por región para identificar oportunidades de distribución",
                }
            ]
        }
    ];

    // [Todas las funciones del agente se mantienen igual - no las copio de nuevo para ahorrar espacio]
    const createAuthenticatedRequest = async (config) => {
        const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
        const my_login = 'cognito-idp.' + config.aws_project_region + '.amazonaws.com/' + config.aws_user_pools_id;

        const credentials = fromCognitoIdentityPool({
            clientConfig: { region: config.aws_project_region },
            identityPoolId: config.aws_cognito_identity_pool_id,
            logins: { [my_login]: authToken }
        });

        const signer = new SignatureV4({
            credentials: credentials,
            region: config.aws_project_region,
            service: 'execute-api',
            sha256: Sha256
        });

        return async (endpoint, method = 'GET', body = null) => {
            const url = new URL(endpoint);

            const request = new HttpRequest({
                hostname: url.hostname,
                path: url.pathname + url.search,
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    host: url.hostname
                },
                body: body ? JSON.stringify(body) : undefined
            });

            const signedRequest = await signer.sign(request);
            const fetchOptions = {
                method: signedRequest.method,
                headers: signedRequest.headers,
                body: signedRequest.body
            };

            const response = await fetch(url.toString(), fetchOptions);
            return response.json();
        };
    };

    const invokeBedrockAgent = async (prompt, sessionId, id_message_bot) => {
        const authToken = (await fetchAuthSession()).tokens?.idToken?.toString();
        const my_login = 'cognito-idp.' + config.aws_project_region + '.amazonaws.com/' + config.aws_user_pools_id;

        const bedrock = new BedrockAgentRuntimeClient({
            region: config.aws_project_region,
            credentials: fromCognitoIdentityPool({
                clientConfig: { region: config.aws_project_region },
                identityPoolId: config.aws_cognito_identity_pool_id,
                logins: { [my_login]: authToken }
            })
        });

        const input = {
            agentId: AGENT_ID,
            agentAliasId: AGENT_ALIAS_ID,
            sessionId,
            inputText: prompt,
            enableTrace: true,
            sessionState: {
                sessionAttributes: {
                    id_message: id_message_bot
                }
            }
        };

        const command = new InvokeAgentCommand(input);
        let completion = "";

        const response = await bedrock.send(command);
        if (response.completion === undefined) {
            throw new Error("Completion is undefined");
        }

        for await (let chunkEvent of response.completion) {
            if (chunkEvent.chunk) {
                const chunk = chunkEvent.chunk;
                const decodedResponse = new TextDecoder("utf-8").decode(chunk.bytes);
                completion += decodedResponse;
            }
        }

        return completion;
    };

    const addMessageBD = async (id_conversation, id_user, message, author, id_message) => {
        try {
            const authenticatedRequest = await createAuthenticatedRequest(config);
            const url = `${baseURL}${endpoint_add_message}`;

            const response = await authenticatedRequest(
                url,
                'POST',
                {
                    id_user: id_user,
                    id_conversation: id_conversation,
                    content: message,
                    author: author,
                    id_message: id_message
                }
            );

            return response.id_message;
        } catch (error) {
            console.error('Error adding message to BD:', error);
            return "None";
        }
    };

    const getQuery = async (id_message) => {
        try {
            const authenticatedRequest = await createAuthenticatedRequest(config);
            const url = `${baseURL}${endpoint_get_sql}?id_message=${encodeURIComponent(id_message)}`;
            const response = await authenticatedRequest(url, 'GET', null);
            return response.messages;
        } catch (error) {
            console.error('Error getting query:', error);
            return [];
        }
    };

    const sendMessageToClaude = async (mensajes, userContent) => {
        const apiKey = apiKeyClaude
        const mensajes_form = mensajes.map(({ question, query }) => ({ question, query }));

        const safeJSONStringify = (obj) => {
            try {
                return JSON.stringify(obj);
            } catch (error) {
                console.warn("Error en serialización JSON:", error);
                const seen = new WeakSet();
                return JSON.stringify(obj, (key, value) => {
                    if (typeof value === 'object' && value !== null) {
                        if (seen.has(value)) {
                            return "[Referencia Circular]";
                        }
                        seen.add(value);
                    }
                    if (typeof value === 'function') {
                        return "[Función]";
                    }
                    if (value instanceof Error) {
                        return {
                            errorType: value.name,
                            errorMessage: value.message
                        };
                    }
                    return value;
                }, 2);
            }
        };

        const content = buildSQLPrompt(userContent, safeJSONStringify(mensajes_form));

        return fetch("https://api.anthropic.com/v1/messages", {
            method: "POST",
            headers: {
                "x-api-key": apiKey,
                "anthropic-version": "2023-06-01",
                "content-type": "application/json",
                "anthropic-dangerous-direct-browser-access": "true"
            },
            body: JSON.stringify({
                model: "claude-sonnet-4-20250514",
                max_tokens: 1000,
                messages: [{
                    role: "user",
                    content: content
                }]
            })
        })
            .then(response => {
                if (!response.ok) {
                    console.error('Error en la API de Claude:', response.statusText);
                    return null;
                }
                return response.json();
            })
            .catch(error => {
                console.error('Error haciendo la petición:', error);
                return null;
            });
    };

    const executeSQL = async (sqlStatement) => {
        try {
            const url = `${baseURLBD}${endpoint_executeSQL}?sqlStatement=${encodeURIComponent(sqlStatement)}`;
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error executing SQL query:', error);
            let errorMessage = "Error al ejecutar la consulta SQL";

            if (error.name === 'AbortError') {
                errorMessage = "La consulta ha excedido el tiempo máximo de ejecución";
            } else if (error.message) {
                errorMessage = error.message;
            }

            return {
                data: [],
                columns: {},
                query: sqlStatement,
                error: errorMessage
            };
        }
    };

    const updateMessageBD = async (id_message, query_sql) => {
        try {
            const authenticatedRequest = await createAuthenticatedRequest(config);
            const url = `${baseURL}/update_query_sql`;

            const response = await authenticatedRequest(
                url,
                'POST',
                {
                    id_message: id_message,
                    query_sql: query_sql
                }
            );

            return response.message;
        } catch (error) {
            console.error('Error updating query_sql:', error);
            return "Failed to update query_sql";
        }
    };

    const handleGenerateExcel = () => {
        if (!queryData || !queryData.data) {
            console.error('No hay datos disponibles para generar el Excel.');
            return;
        }

        const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '');
        const worksheet = XLSX.utils.json_to_sheet(queryData.data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Datos_IQVIA');
        XLSX.writeFile(workbook, `analisis_iqvia_${timestamp}.xlsx`);
    };

    const handleQueryClick = async (query) => {
        const queryText = typeof query === 'object' ? query.text : query;

        setSelectedQuery(queryText);
        setModalIsOpen(true);
        setLoading(true);
        setAgentResponse('');
        setQueryData(null);
        setVizLoading(false);

        try {
            if (existsInCache(queryText)) {
                const cachedData = getFromCache(queryText);
                setAgentResponse(cachedData.agentResponse);
                setCurrentMessageId(cachedData.messageId);

                if (cachedData.queryData) {
                    setQueryData(cachedData.queryData);
                }

                setLoading(false);
                return;
            }

            const id_message_client = uuidv4();
            const id_message_bot = uuidv4();
            setCurrentMessageId(id_message_bot);

            await addMessageBD(id_conversation, id_user, queryText, "Client", id_message_client);

            const response = await invokeBedrockAgent(queryText, id_conversation, id_message_bot);

            if (!response || response === "Sorry, I am unable to assist you with this request.") {
                throw new Error("El agente no pudo procesar la consulta");
            }

            setAgentResponse(response);

            await addMessageBD(id_conversation, id_user, response, "Bot", id_message_bot);

            saveToCache(queryText, {
                agentResponse: response,
                messageId: id_message_bot,
                queryData: null,
                sqlQuery: null
            });

        } catch (error) {
            console.error('Error processing query:', error);
            setAgentResponse("Lo siento, hubo un error al procesar tu consulta. Por favor, intenta de nuevo.");
        } finally {
            setLoading(false);
        }
    };

    const handleVizualization = async () => {
        if (!currentMessageId) return;

        setVizLoading(true);
        setQueryData(null);

        try {
            const cachedData = getFromCache(selectedQuery);
            if (cachedData && cachedData.queryData && cachedData.sqlQuery) {
                setQueryData(cachedData.queryData);
                setVizLoading(false);
                return;
            }

            const mensajes_anteriores = await getQuery(currentMessageId);

            const respuestaClaude = await sendMessageToClaude(mensajes_anteriores, selectedQuery);

            if (!respuestaClaude || !respuestaClaude.content?.[0]?.text) {
                throw new Error("No se pudo generar la consulta SQL");
            }

            let sqlQuery = respuestaClaude.content[0].text;

            const validator = new SQLValidator();
            sqlQuery = validator.cleanMarkdown(sqlQuery);

            await updateMessageBD(currentMessageId, sqlQuery);

            const records = await executeSQL(sqlQuery);

            if (records.error) {
                throw new Error(records.error);
            }

            const newQueryData = {
                data: records.data || [],
                columns: records.columns || {},
                query: sqlQuery,
                column_types: records.column_types || {},
                hasError: false
            };

            setQueryData(newQueryData);

            if (cachedData) {
                saveToCache(selectedQuery, {
                    ...cachedData,
                    queryData: newQueryData,
                    sqlQuery: sqlQuery
                });
            }

        } catch (error) {
            console.error('Error in visualization:', error);
            const errorData = {
                data: [],
                columns: {},
                query: "",
                column_types: {},
                hasError: true,
                error: error.message || "No se puede generar la gráfica. Por favor, intenta reformular tu pregunta."
            };

            setQueryData(errorData);

            const cachedData = getFromCache(selectedQuery);
            if (cachedData) {
                saveToCache(selectedQuery, {
                    ...cachedData,
                    queryData: errorData,
                    sqlQuery: null
                });
            }
        } finally {
            setVizLoading(false);
        }
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setSelectedQuery(null);
        setAgentResponse('');
        setQueryData(null);
        setCurrentMessageId(null);
        setLoading(false);
        setVizLoading(false);
    };

    const dataToShow = activeTab === 'fases' ? consultasPorFase : consultasPorRol;
    const iconsToUse = activeTab === 'fases' ? faseIcons : roleIcons;

    return (
        <div className="roadmap-container">
            {/* Tabs Header */}
            <div className="tabs-header">
                <div className="tabs-nav">
                    <button
                        className={`tab-button ${activeTab === 'fases' ? 'active' : ''}`}
                        onClick={() => setActiveTab('fases')}
                    >
                        <Search size={20} />
                        Fases de Análisis
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'roles' ? 'active' : ''}`}
                        onClick={() => setActiveTab('roles')}
                    >
                        <Users size={20} />
                        Roadmap por Roles
                    </button>
                </div>
            </div>

            {/* Header */}
            <div className="header">
                <div className="title-section">
                    <h1>{activeTab === 'fases' ? 'Fases de Análisis con IA IQVIA' : 'Roadmap de Consultas por Roles Farmacéuticos'}</h1>
                    <p>{activeTab === 'fases' ? 'Metodología estructurada para análisis de mercado farmacéutico mexicano con datos de prescripción y ventas' : 'Guía completa por roles para maximizar insights del mercado farmacéutico'}</p>
                </div>

                <div className="progress-section">
                    <div className="progress-stats">
                        <div className="stat">
                            <span className="stat-number">{completedSteps.size}</span>
                            <span className="stat-label">Completados</span>
                        </div>
                        <div className="stat">
                            <span className="stat-number">{dataToShow.length - completedSteps.size}</span>
                            <span className="stat-label">Pendientes</span>
                        </div>
                        <div className="stat">
                            <span className="stat-number">{Math.round((completedSteps.size / dataToShow.length) * 100)}%</span>
                            <span className="stat-label">Progreso</span>
                        </div>
                    </div>
                    <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${(completedSteps.size / dataToShow.length) * 100}%` }} />
                    </div>
                </div>
            </div>

            <div className="main-content">
                {/* Timeline Roadmap */}
                <div className="timeline-container">
                    <div className="timeline-line" />

                    {dataToShow.map((section, index) => {
                        const IconComponent = iconsToUse[section.title];
                        const isCompleted = completedSteps.has(index);
                        const isExpanded = expandedSections.has(index);

                        return (
                            <div key={index} className={`timeline-item ${isCompleted ? 'completed' : ''}`}>
                                <div className="timeline-node">
                                    <IconComponent size={24} color="white" />
                                </div>

                                <div className="timeline-card">
                                    <div className="card-header">
                                        <div className="role-info">
                                            <div className="role-title-row">
                                                <h3>{section.title}</h3>
                                                <div className="badges">
                                                    <span className={`priority-badge priority-${section.priority.toLowerCase()}`}>
                                                        {section.priority}
                                                    </span>
                                                    <span className="time-badge">{section.timeEstimate}</span>
                                                </div>
                                            </div>
                                            <p className="role-description">{section.description}</p>
                                            <div className="role-stats">
                                                <span className="query-count">{section.consultas.length} consultas disponibles</span>
                                            </div>
                                        </div>

                                        <div className="card-actions">
                                            <button
                                                className={`completion-btn ${isCompleted ? 'completed' : ''}`}
                                                onClick={() => {
                                                    const newCompleted = new Set(completedSteps);
                                                    if (newCompleted.has(index)) {
                                                        newCompleted.delete(index);
                                                    } else {
                                                        newCompleted.add(index);
                                                    }
                                                    setCompletedSteps(newCompleted);
                                                }}
                                            >
                                                {isCompleted ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                                                {isCompleted ? 'Completado' : 'Pendiente'}
                                            </button>

                                            <button
                                                className="expand-btn"
                                                onClick={() => {
                                                    const newExpanded = new Set(expandedSections);
                                                    if (newExpanded.has(index)) {
                                                        newExpanded.delete(index);
                                                    } else {
                                                        newExpanded.add(index);
                                                    }
                                                    setExpandedSections(newExpanded);
                                                }}
                                            >
                                                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                            </button>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="card-content">
                                            <h4>Consultas Recomendadas:</h4>
                                            <div className="queries-list">
                                                {section.consultas.map((consulta, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="query-item"
                                                        onClick={() => handleQueryClick(consulta)}
                                                    >
                                                        <div className="query-number">{idx + 1}</div>
                                                        <p>{activeTab === 'fases' ? consulta.text : consulta}</p>
                                                        <div className="query-arrow">→</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Modal de resultados */}
            {modalIsOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Consulta IQVIA: {selectedQuery}</h3>
                            <button className="close-btn" onClick={closeModal}>
                                <X size={24} />
                            </button>
                        </div>

                        <div className="modal-body">
                            {loading ? (
                                <div className="loading-container">
                                    <Loader2 className="animate-spin" size={40} />
                                    <p>Procesando consulta farmacéutica con IA...</p>
                                </div>
                            ) : (
                                <>
                                    <div className="agent-response-section">
                                        <h4>Análisis con IA</h4>
                                        <div className="agent-response-content">
                                            <ReactMarkdown
                                                components={{
                                                    code({ node, inline, className, children, ...props }) {
                                                        const match = /language-(\w+)/.exec(className || '');
                                                        return !inline && match ? (
                                                            <SyntaxHighlighter
                                                                style={tomorrow}
                                                                language={match[1]}
                                                                PreTag="div"
                                                                {...props}
                                                            >
                                                                {String(children).replace(/\n$/, '')}
                                                            </SyntaxHighlighter>
                                                        ) : (
                                                            <code className={className} {...props}>
                                                                {children}
                                                            </code>
                                                        );
                                                    }
                                                }}
                                            >
                                                {agentResponse}
                                            </ReactMarkdown>
                                        </div>
                                    </div>

                                    <div className="visualization-section">
                                        <button
                                            className="visualize-btn"
                                            onClick={handleVizualization}
                                            disabled={vizLoading}
                                        >
                                            {vizLoading ? (
                                                <>
                                                    <Loader2 className="animate-spin" size={16} />
                                                    Generando visualización...
                                                </>
                                            ) : (
                                                <>
                                                    <BarChart3 size={20} />
                                                    Visualizar Datos
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {queryData && (
                                        <div className="visualization-results">
                                            {queryData.hasError ? (
                                                <div className="error-message">
                                                    <h4>Error en la visualización</h4>
                                                    <p>{queryData.error}</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="chart-container">
                                                        <h4>Visualización de Datos IQVIA</h4>
                                                        <ChartWithControls
                                                            columns={queryData.columns || {}}
                                                            data={queryData.data || []}
                                                            column_types={queryData.column_types || {}}
                                                            hasError={queryData.hasError || false}
                                                            error={queryData.error || null}
                                                        />

                                                        <div className="excel-download">
                                                            <button onClick={handleGenerateExcel} className="excel-btn">
                                                                <Download size={16} />
                                                                Descargar Excel
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {queryData.data && queryData.data.length > 0 && (
                                                        <div className="data-table">
                                                            <h4>Vista de Datos</h4>
                                                            <DataTable
                                                                columns={queryData.columns || {}}
                                                                data={queryData.data || []}
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Los estilos CSS se mantienen exactamente igual */}
            <style jsx>{`
                .app-main {
                    overflow-y: auto!important;
                }
                
                .roadmap-container {
                    min-height: 100dvh;
                    overflow-y: auto;
                    overflow-x: hidden;
                    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }
                
                .tabs-header {
                    background: white;
                    border-bottom: 1px solid #e2e8f0;
                    padding: 0 2rem;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                }

                .tabs-nav {
                    max-width: 1200px;
                    margin: 0 auto;
                    display: flex;
                    gap: 0;
                }

                .tab-button {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 1rem 1.5rem;
                    border: none;
                    background: transparent;
                    color: #64748b;
                    cursor: pointer;
                    border-bottom: 3px solid transparent;
                    transition: all 0.3s ease;
                    font-weight: 500;
                }

                .tab-button:hover {
                    color: #0891b2;
                    background: #f0f9ff;
                }

                .tab-button.active {
                    color: #0891b2;
                    border-bottom-color: #0891b2;
                    background: #f0f9ff;
                }

                .main-content {
                    padding: 2rem;
                    max-width: 1200px;
                    margin: 0 auto;
                }

                .header {
                    background: white;
                    border-radius: 16px;
                    padding: 2rem;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                    border: 1px solid #e0f2fe;
                    margin-bottom: 2rem;
                    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
                }

                .title-section h1 {
                    color: #0f172a;
                    font-size: 2.5rem;
                    font-weight: 700;
                    margin: 0 0 0.5rem 0;
                    background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .title-section p {
                    color: #64748b;
                    font-size: 1.1rem;
                    margin: 0;
                }

                .progress-section {
                    display: flex;
                    align-items: center;
                    gap: 2rem;
                    margin-top: 2rem;
                }

                .progress-stats {
                    display: flex;
                    gap: 2rem;
                }

                .stat {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .stat-number {
                    font-size: 2rem;
                    font-weight: 700;
                    color: #0891b2;
                }

                .stat-label {
                    font-size: 0.9rem;
                    color: #64748b;
                    text-transform: uppercase;
                    font-weight: 500;
                }

                .progress-bar {
                    flex: 1;
                    height: 8px;
                    background: #e2e8f0;
                    border-radius: 4px;
                    overflow: hidden;
                }

                .progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #0891b2, #06b6d4);
                    transition: width 0.5s ease;
                }

                .timeline-container {
                    position: relative;
                }

                .timeline-line {
                    position: absolute;
                    left: 30px;
                    top: 0;
                    bottom: 0;
                    width: 2px;
                    background: linear-gradient(180deg, #0891b2 0%, #06b6d4 100%);
                    z-index: 1;
                }

                .timeline-item {
                    display: flex;
                    align-items: flex-start;
                    margin-bottom: 2rem;
                    position: relative;
                }

                .timeline-item.completed .timeline-card {
                    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                    border-color: #0891b2;
                    box-shadow: 0 4px 12px rgba(8, 145, 178, 0.15);
                }

                .timeline-node {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    z-index: 2;
                    box-shadow: 0 4px 12px rgba(8, 145, 178, 0.3);
                    flex-shrink: 0;
                    margin-right: 2rem;
                }

                .timeline-card {
                    flex: 1;
                    background: white;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    overflow: hidden;
                    transition: all 0.3s ease;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                }

                .timeline-card:hover {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                    transform: translateY(-1px);
                }

                .card-header {
                    padding: 1.5rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 1rem;
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                }

                .role-info {
                    flex: 1;
                }

                .role-title-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                }

                .role-info h3 {
                    font-size: 1.4rem;
                    font-weight: 600;
                    margin: 0;
                    color: #0891b2;
                }

                .badges {
                    display: flex;
                    gap: 0.5rem;
                }

                .priority-badge {
                    padding: 0.25rem 0.75rem;
                    color: white;
                    font-size: 0.75rem;
                    font-weight: 600;
                    border-radius: 12px;
                    text-transform: uppercase;
                }

                .priority-alta { 
                    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); 
                }
                .priority-media { 
                    background: linear-gradient(135deg, #ea580c 0%, #c2410c 100%); 
                }
                .priority-baja { 
                    background: linear-gradient(135deg, #16a34a 0%, #15803d 100%); 
                }

                .time-badge {
                    padding: 0.25rem 0.75rem;
                    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
                    color: #475569;
                    font-size: 0.75rem;
                    font-weight: 500;
                    border-radius: 12px;
                }

                .role-description {
                    color: #64748b;
                    margin: 0.5rem 0;
                    line-height: 1.5;
                }

                .query-count {
                    color: #0891b2;
                    font-size: 0.9rem;
                    font-weight: 500;
                }

                .card-actions {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    align-items: flex-end;
                }

                .completion-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    border: 2px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    font-size: 0.9rem;
                    font-weight: 500;
                }

                .completion-btn:hover {
                    border-color: #0891b2;
                    color: #0891b2;
                }

                .completion-btn.completed {
                    background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
                    border-color: #0891b2;
                    color: white;
                }

                .expand-btn {
                    padding: 0.5rem;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .expand-btn:hover {
                    background: #f0f9ff;
                    border-color: #0891b2;
                    color: #0891b2;
                }

                .card-content {
                    padding: 0 1.5rem 1.5rem;
                    border-top: 1px solid #f1f5f9;
                    animation: slideDown 0.3s ease;
                }

                .card-content h4 {
                    color: #374151;
                    margin: 1rem 0;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                .queries-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }

                .query-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 1rem;
                    padding: 1rem;
                    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                    border-radius: 8px;
                    border-left: 4px solid #0891b2;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                    border: 1px solid transparent;
                }

                .query-item:hover {
                    background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
                    transform: translateX(4px);
                    border-color: #0891b2;
                    box-shadow: 0 4px 12px rgba(8, 145, 178, 0.25);
                }

                .query-number {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.8rem;
                    font-weight: 600;
                    flex-shrink: 0;
                }

                .query-item p {
                    margin: 0;
                    color: #374151;
                    line-height: 1.5;
                    flex: 1;
                }

                .query-arrow {
                    color: #0891b2;
                    font-weight: bold;
                    font-size: 1.2rem;
                }

                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    padding: 2rem;
                    animation: fadeIn 0.3s ease;
                }

                .modal-content {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    max-width: 1000px;
                    width: 100%;
                    max-height: 90vh;
                    overflow: hidden;
                    animation: slideUp 0.3s ease;
                }

                .modal-header {
                    padding: 2rem 2rem 1rem;
                    border-bottom: 1px solid #e2e8f0;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                }

                .modal-header h3 {
                    color: #0f172a;
                    margin: 0;
                    font-size: 1.5rem;
                    font-weight: 700;
                }

                .close-btn {
                    padding: 0.5rem;
                    border: none;
                    background: rgba(255, 255, 255, 0.8);
                    color: #64748b;
                    cursor: pointer;
                    border-radius: 8px;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .close-btn:hover {
                    background: #e2e8f0;
                    color: #374151;
                    transform: scale(1.05);
                }

                .modal-body {
                    padding: 2rem;
                    max-height: calc(90vh - 120px);
                    overflow-y: auto;
                }

                .loading-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 3rem;
                    text-align: center;
                }

                .loading-container p {
                    color: #0891b2;
                    font-weight: 500;
                    margin-top: 1rem;
                }

                .animate-spin {
                    animation: spin 1s linear infinite;
                    color: #0891b2;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                .agent-response-section {
                    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
                    padding: 1.5rem;
                    border-radius: 12px;
                    margin-bottom: 2rem;
                    border-left: 4px solid #10b981;
                }

                .agent-response-section h4 {
                    margin: 0 0 1rem 0;
                    color: #047857;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                .agent-response-content {
                    color: #047857;
                    line-height: 1.6;
                }

                .visualization-section {
                    margin: 2rem 0;
                    text-align: center;
                }

                .visualize-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 500;
                    transition: all 0.3s ease;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                }

                .visualize-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
                    transform: translateY(-1px);
                    box-shadow: 0 6px 12px -1px rgba(0, 0, 0, 0.15);
                }

                .visualize-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .visualization-results {
                    margin-top: 2rem;
                    border-top: 1px solid #e2e8f0;
                    padding-top: 2rem;
                }

                .error-message {
                    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    padding: 1rem;
                    color: #dc2626;
                }

                .chart-container {
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                }

                .chart-container h4 {
                    margin: 0 0 1rem 0;
                    color: #374151;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                .excel-download {
                    margin-top: 1rem;
                    text-align: center;
                }

                .excel-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
                    color: white;
                    border: none;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 0.9rem;
                    transition: all 0.3s ease;
                }

                .excel-btn:hover {
                    background: linear-gradient(135deg, #15803d 0%, #166534 100%);
                    transform: translateY(-1px);
                }

                .data-table {
                    margin-top: 2rem;
                }

                .data-table h4 {
                    margin: 0 0 1rem 0;
                    color: #374151;
                    font-size: 1.1rem;
                    font-weight: 600;
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @media (max-width: 768px) {
                    .roadmap-container {
                        padding: 0;
                    }
                    
                    .main-content {
                        padding: 1rem;
                    }
                    
                    .timeline-line {
                        left: 20px;
                    }
                    
                    .timeline-node {
                        width: 40px;
                        height: 40px;
                        margin-right: 1rem;
                    }
                    
                    .card-header {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    
                    .card-actions {
                        flex-direction: row;
                        width: 100%;
                        justify-content: space-between;
                    }
                    
                    .role-title-row {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 0.5rem;
                    }

                    .modal-overlay {
                        padding: 1rem;
                    }
                    
                    .modal-content {
                        max-height: 95vh;
                    }
                    
                    .modal-header {
                        padding: 1.5rem 1.5rem 1rem;
                    }
                    
                    .modal-body {
                        padding: 1.5rem;
                    }
                }
            `}</style>
        </div>
    );
};

export default GuiaInteractiva;